import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useI18n } from '@/context/I18nContext';
import { getDeviationColorRgb, getMetricUnit } from '@/lib/map/lapDiffMath';
import type { LapDiffMetric, PointTimeSeriesEntry } from '@/lib/map/lapDiffMath';
import type { TrackAnnotation } from '@/lib/spline';

interface LapDiffTimeChartProps {
  timeSeries: PointTimeSeriesEntry[];
  metric: LapDiffMetric;
  viewLap: 'A' | 'B';
  hoveredProgress: number | null;
  onHoverProgress: (progress: number | null) => void;
  anchors: [number, number] | null;
  onChangeAnchors: (anchors: [number, number] | null) => void;
  onSelectProgress: (progress: number) => void;
  trackAnnotations?: TrackAnnotation[];
  labelA?: string;
  labelB?: string;
}

const PAD = { top: 24, right: 18, bottom: 28, left: 58 };

export function LapDiffTimeChart({
  timeSeries,
  metric,
  viewLap,
  hoveredProgress,
  onHoverProgress,
  anchors,
  onChangeAnchors,
  onSelectProgress,
  trackAnnotations = [],
  labelA,
  labelB,
}: LapDiffTimeChartProps) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 600, height: 190 });
  const draggingAnchorRef = useRef<0 | 1 | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = Math.max(260, Math.floor(entry.contentRect.width));
        setCanvasSize({ width: w, height: 190 });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const maxDistanceM = useMemo(() => {
    if (timeSeries.length === 0) return 0;
    return timeSeries[timeSeries.length - 1].distanceM;
  }, [timeSeries]);

  const getPointRawDiffA = useCallback(
    (entry: PointTimeSeriesEntry): number => {
      if (metric === 'time') return entry.cumDiffAMs / 1000; // seconds
      if (metric === 'speed') return entry.speedDiffKmh;
      if (metric === 'rpm') return entry.rpmDiff;
      return entry.accelDiff;
    },
    [metric]
  );

  const maxAbsColorDriver = useMemo(() => {
    let maxVal = 0;
    for (let i = 1; i < timeSeries.length; i++) {
      const absVal =
        metric === 'time'
          ? Math.abs(timeSeries[i].segDiffAMs)
          : Math.abs(getPointRawDiffA(timeSeries[i]));
      if (absVal > maxVal) maxVal = absVal;
    }
    return maxVal > 0 ? maxVal : 1;
  }, [timeSeries, metric, getPointRawDiffA]);

  const progressToX = useCallback(
    (progress: number, plotW: number): number => {
      if (!Number.isFinite(progress) || timeSeries.length === 0) {
        return PAD.left;
      }
      const clampedProgress = Math.max(0, Math.min(1, progress));
      if (maxDistanceM <= 0) {
        return PAD.left + clampedProgress * plotW;
      }
      const idxFloat = Math.max(0, Math.min(timeSeries.length - 1, clampedProgress * (timeSeries.length - 1)));
      const i0 = Math.floor(idxFloat);
      const i1 = Math.min(timeSeries.length - 1, i0 + 1);
      const u = idxFloat - i0;
      const d0 = timeSeries[i0]?.distanceM ?? 0;
      const d1 = timeSeries[i1]?.distanceM ?? d0;
      const dist = d0 + u * (d1 - d0);
      return PAD.left + (dist / maxDistanceM) * plotW;
    },
    [timeSeries, maxDistanceM]
  );

  const cornerMarkers = useMemo(() => {
    if (!trackAnnotations || trackAnnotations.length === 0 || timeSeries.length === 0) {
      return [];
    }
    const corners = trackAnnotations.filter(a => a.type === 'corner');
    const result: Array<{ progress: number; label: string }> = [];
    corners.forEach((ann, idx) => {
      const pt = ann.apex || ann.location || ann.entry;
      if (!pt || !Number.isFinite(pt.lat) || !Number.isFinite(pt.lng)) return;
      let bestIdx = 0;
      let bestDistSq = Infinity;
      const cosLat = Math.cos((pt.lat * Math.PI) / 180);
      for (let i = 0; i < timeSeries.length; i++) {
        const dx = (timeSeries[i].lng - pt.lng) * cosLat;
        const dy = timeSeries[i].lat - pt.lat;
        const dSq = dx * dx + dy * dy;
        if (dSq < bestDistSq) {
          bestDistSq = dSq;
          bestIdx = i;
        }
      }
      const cornerNum = ann.orderIndex > 0 ? ann.orderIndex : idx + 1;
      result.push({
        progress: timeSeries[bestIdx].progress,
        label: `T${cornerNum}`,
      });
    });
    return result;
  }, [trackAnnotations, timeSeries]);

  const xToProgress = useCallback(
    (canvasX: number, plotW: number): number => {
      const relX = Math.max(0, Math.min(1, (canvasX - PAD.left) / Math.max(1, plotW)));
      if (timeSeries.length === 0 || maxDistanceM <= 0) {
        return relX;
      }
      const targetDist = relX * maxDistanceM;
      let lo = 0;
      let hi = timeSeries.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (timeSeries[mid].distanceM < targetDist) {
          lo = mid + 1;
        } else {
          hi = mid;
        }
      }
      if (lo === 0) return timeSeries[0].progress;
      const d0 = timeSeries[lo - 1].distanceM;
      const d1 = timeSeries[lo].distanceM;
      const u = d1 > d0 ? (targetDist - d0) / (d1 - d0) : 0;
      const p0 = timeSeries[lo - 1].progress;
      const p1 = timeSeries[lo].progress;
      return Math.max(0, Math.min(1, p0 + u * (p1 - p0)));
    },
    [timeSeries, maxDistanceM]
  );

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || timeSeries.length < 2) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const W = canvasSize.width;
    const H = canvasSize.height;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, W, H);

    const plotW = W - PAD.left - PAD.right;
    const plotH = H - PAD.top - PAD.bottom;
    if (plotW <= 10 || plotH <= 10) return;

    const sign = viewLap === 'A' ? 1 : -1;

    // Compute Y bounds (always include 0)
    let minVal = 0;
    let maxVal = 0;
    for (let i = 0; i < timeSeries.length; i++) {
      const val = getPointRawDiffA(timeSeries[i]) * sign;
      if (val < minVal) minVal = val;
      if (val > maxVal) maxVal = val;
    }
    const minSpan = metric === 'rpm' ? 10 : metric === 'speed' ? 1.0 : 0.05;
    const rawSpan = Math.max(maxVal - minVal, minSpan);
    const yMin = minVal - rawSpan * 0.12;
    const yMax = maxVal + rawSpan * 0.12;
    const ySpan = Math.max(yMax - yMin, minSpan);

    const toY = (val: number) => PAD.top + (1 - (val - yMin) / ySpan) * plotH;
    const yZero = toY(0);

    const formatYTick = (val: number): string => {
      if (metric === 'time') {
        return `${val > 0.0005 ? '+' : ''}${val.toFixed(2)}s`;
      }
      if (metric === 'rpm') {
        const rounded = Math.round(val);
        return `${rounded > 0 ? '+' : ''}${rounded}`;
      }
      return `${val > 0.005 ? '+' : ''}${val.toFixed(1)}`;
    };

    // Horizontal grid lines & Y-axis labels
    const yTicks = 4;
    ctx.font = '500 10px ui-monospace, SFMono-Regular, Menlo, monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    for (let i = 0; i <= yTicks; i++) {
      const val = yMin + (i / yTicks) * ySpan;
      const y = toY(val);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD.left, y);
      ctx.lineTo(W - PAD.right, y);
      ctx.stroke();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
      ctx.fillText(formatYTick(val), PAD.left - 6, y);
    }

    // Zero baseline
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(PAD.left, yZero);
    ctx.lineTo(W - PAD.right, yZero);
    ctx.stroke();
    ctx.restore();

    // X-axis distance grid & labels
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const xTicks = 5;
    for (let i = 0; i <= xTicks; i++) {
      const frac = i / xTicks;
      const x = PAD.left + frac * plotW;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.04)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, PAD.top);
      ctx.lineTo(x, PAD.top + plotH);
      ctx.stroke();

      const label = maxDistanceM > 0
        ? `${Math.round(frac * maxDistanceM)}m`
        : `${Math.round(frac * 100)}%`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.42)';
      ctx.fillText(label, x, PAD.top + plotH + 7);
    }

    // Corner annotations along the top
    if (cornerMarkers.length > 0) {
      ctx.save();
      ctx.font = '600 9px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      for (const marker of cornerMarkers) {
        const x = progressToX(marker.progress, plotW);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
        ctx.setLineDash([2, 3]);
        ctx.beginPath();
        ctx.moveTo(x, PAD.top);
        ctx.lineTo(x, PAD.top + plotH);
        ctx.stroke();

        ctx.fillStyle = 'rgba(255, 255, 255, 0.38)';
        ctx.fillText(marker.label, x, PAD.top - 3);
      }
      ctx.restore();
    }

    const hasAnchorRange = anchors !== null;
    const sMin = hasAnchorRange ? Math.min(anchors[0], anchors[1]) : 0;
    const sMax = hasAnchorRange ? Math.max(anchors[0], anchors[1]) : 1;

    // Draw per-segment fill polygons and curve strokes
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (let i = 1; i < timeSeries.length; i++) {
      const prev = timeSeries[i - 1];
      const curr = timeSeries[i];

      const x0 = maxDistanceM > 0
        ? PAD.left + (prev.distanceM / maxDistanceM) * plotW
        : PAD.left + prev.progress * plotW;
      const x1 = maxDistanceM > 0
        ? PAD.left + (curr.distanceM / maxDistanceM) * plotW
        : PAD.left + curr.progress * plotW;

      const v0 = getPointRawDiffA(prev) * sign;
      const v1 = getPointRawDiffA(curr) * sign;
      const y0 = toY(v0);
      const y1 = toY(v1);

      // aAdvantage > 0 means Lap A is faster/better (Red #ef4444); < 0 means Lap B is faster/better (Blue #3b82f6)
      const aAdvantage =
        metric === 'time'
          ? -curr.segDiffAMs
          : 0.5 * (getPointRawDiffA(prev) + getPointRawDiffA(curr));
      const [r, g, b] = getDeviationColorRgb(aAdvantage, maxAbsColorDriver);
      const inRange = !hasAnchorRange || (curr.progress >= sMin && curr.progress <= sMax);

      // Area fill to zero baseline
      ctx.beginPath();
      ctx.moveTo(x0, yZero);
      ctx.lineTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineTo(x1, yZero);
      ctx.closePath();
      ctx.fillStyle = inRange ? `rgba(${r}, ${g}, ${b}, 0.22)` : 'rgba(255, 255, 255, 0.03)';
      ctx.fill();

      // Curve segment stroke
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.lineWidth = inRange ? 2.5 : 1.5;
      ctx.strokeStyle = inRange ? `rgb(${r}, ${g}, ${b})` : 'rgba(255, 255, 255, 0.16)';
      ctx.stroke();
    }

    // Draw anchor range bounds if active
    if (anchors) {
      const drawAnchorLine = (s: number) => {
        const x = progressToX(s, plotW);
        ctx.save();
        ctx.strokeStyle = '#7c5cfc';
        ctx.lineWidth = 1.75;
        ctx.beginPath();
        ctx.moveTo(x, PAD.top);
        ctx.lineTo(x, PAD.top + plotH);
        ctx.stroke();

        ctx.fillStyle = '#7c5cfc';
        ctx.beginPath();
        ctx.arc(x, PAD.top + 6, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.25;
        ctx.stroke();
        ctx.restore();
      };
      drawAnchorLine(anchors[0]);
      drawAnchorLine(anchors[1]);
    }

    // Draw synchronized hover cursor & value badge
    if (hoveredProgress !== null && Number.isFinite(hoveredProgress) && timeSeries.length > 0) {
      const ptIdx = Math.min(
        timeSeries.length - 1,
        Math.max(0, Math.round(hoveredProgress * (timeSeries.length - 1)))
      );
      const entry = timeSeries[ptIdx];
      if (entry) {
        const hx = maxDistanceM > 0
          ? PAD.left + (entry.distanceM / maxDistanceM) * plotW
          : PAD.left + entry.progress * plotW;
        const val = getPointRawDiffA(entry) * sign;
        const hy = toY(val);
        const aAdvantage =
          metric === 'time' ? -entry.segDiffAMs : getPointRawDiffA(entry);
        const [r, g, b] = getDeviationColorRgb(aAdvantage, maxAbsColorDriver);

        ctx.save();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(hx, PAD.top);
        ctx.lineTo(hx, PAD.top + plotH);
        ctx.stroke();
        ctx.restore();

        // Point highlight
        ctx.beginPath();
        ctx.arc(hx, hy, 5.5, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(hx, hy, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.fill();

        // Floating badge
        const distText = `${Math.round(entry.distanceM)}m`;
        let badgeText: string;
        if (metric === 'time') {
          const cumSec = (entry.cumDiffAMs * sign) / 1000;
          const segMs = entry.segDiffAMs * sign;
          const cumText = `${cumSec >= 0 ? '+' : ''}${cumSec.toFixed(2)}s`;
          const segText = `${segMs >= 0 ? '+' : ''}${segMs.toFixed(1)}ms`;
          badgeText = `${distText} · ${cumText} (${segText})`;
        } else {
          const unit = getMetricUnit(metric);
          const decimals = metric === 'rpm' ? 0 : 1;
          const valText = `${val >= 0 ? '+' : ''}${val.toFixed(decimals)} ${unit}`;
          badgeText = `${distText} · ${valText}`;
        }

        ctx.font = '600 10px ui-monospace, SFMono-Regular, Menlo, monospace';
        const textWidth = ctx.measureText(badgeText).width;
        const badgeW = textWidth + 14;
        const badgeH = 20;
        const badgeX = Math.max(
          PAD.left + 2,
          Math.min(W - PAD.right - badgeW - 2, hx - badgeW / 2)
        );
        const badgeY = hy > PAD.top + 30 ? hy - 26 : hy + 8;

        ctx.fillStyle = 'rgba(15, 17, 23, 0.92)';
        ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 5);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#f8fafc';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(badgeText, badgeX + 7, badgeY + badgeH / 2);
      }
    }
  }, [
    timeSeries,
    metric,
    viewLap,
    canvasSize,
    maxDistanceM,
    maxAbsColorDriver,
    getPointRawDiffA,
    anchors,
    hoveredProgress,
    cornerMarkers,
    progressToX,
  ]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const plotW = canvasSize.width - PAD.left - PAD.right;

      if (anchors) {
        const ax0 = progressToX(anchors[0], plotW);
        const ax1 = progressToX(anchors[1], plotW);
        if (Math.abs(x - ax0) <= 12) {
          draggingAnchorRef.current = 0;
          e.currentTarget.setPointerCapture(e.pointerId);
          return;
        }
        if (Math.abs(x - ax1) <= 12) {
          draggingAnchorRef.current = 1;
          e.currentTarget.setPointerCapture(e.pointerId);
          return;
        }
      }

      const s = xToProgress(x, plotW);
      if (!anchors) {
        onSelectProgress(s);
      }
    },
    [anchors, canvasSize.width, progressToX, xToProgress, onSelectProgress]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const plotW = canvasSize.width - PAD.left - PAD.right;
      const s = xToProgress(x, plotW);

      if (draggingAnchorRef.current !== null && anchors) {
        const next: [number, number] =
          draggingAnchorRef.current === 0 ? [s, anchors[1]] : [anchors[0], s];
        onChangeAnchors(next);
      }

      onHoverProgress(s);
    },
    [anchors, canvasSize.width, xToProgress, onChangeAnchors, onHoverProgress]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (draggingAnchorRef.current !== null) {
        draggingAnchorRef.current = null;
        try {
          e.currentTarget.releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
      }
    },
    []
  );

  const handlePointerLeave = useCallback(() => {
    if (draggingAnchorRef.current === null) {
      onHoverProgress(null);
    }
  }, [onHoverProgress]);

  if (timeSeries.length < 2) return null;

  const chartTitle =
    metric === 'time'
      ? t('lapdiff.timeChartTitle')
      : metric === 'speed'
        ? `${t('lapdiff.tabSpeed')} Δ (${getMetricUnit('speed')})`
        : metric === 'rpm'
          ? `${t('lapdiff.tabRpm')} Δ (${getMetricUnit('rpm')})`
          : `${t('lapdiff.tabAcceleration')} Δ (${getMetricUnit('acceleration')})`;

  const displayLabelA = labelA || t('lapdiff.viewLapA');
  const displayLabelB = labelB || t('lapdiff.viewLapB');

  return (
    <div
      ref={containerRef}
      className="card w-full max-w-[600px] mx-auto overflow-hidden border border-white/10"
    >
      <div className="px-4 py-2.5 border-b border-border-subtle bg-bg-surface/50 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
            {chartTitle}
          </span>
          <span className="text-[0.68rem] text-text-muted">
            ({viewLap === 'A' ? displayLabelA : displayLabelB})
          </span>
        </div>
        <div className="flex items-center gap-3 text-[0.68rem] text-text-secondary">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#ef4444' }} />
            {displayLabelA}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#3b82f6' }} />
            {displayLabelB}
          </span>
        </div>
      </div>
      <div className="p-2 bg-bg-raised/40">
        <canvas
          ref={canvasRef}
          style={{ width: `${canvasSize.width}px`, height: `${canvasSize.height}px` }}
          className="block w-full select-none cursor-crosshair"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
        />
      </div>
    </div>
  );
}

